import {
  Checkbox,
  COLUMN_GROUP_NATIVE_TYPE,
  FormControlSizes,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  ProductTypeEnum,
  Text,
  Toggle,
  useCallbackRef,
  useMarketAccountsContext,
  useMixpanel,
  useSecuritiesContext,
  useUnifiedLiquidity,
  type ColumnGroup,
  type MarketAccount,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import type { GridApi, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { compact, isEqual, noop, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { MarketSelectorRow } from '../blotter/types';
import type {
  MarketSelectorAPI,
  MarketSelectorExtensionFactory,
  MarketSelectorItem,
  MarketSelectorUnifiedLiquiditySelection,
} from '../types';
import { getUnifiedLiquidityMarketAccountTooltipGroup } from '../unifiedLiquidityMarketTooltip';
import { UnifiedLiquidityTooltipIndicator } from '../UnifiedLiquidityTooltipIndicator';
import {
  getUnifiedLiquiditySelectionKey,
  hasUnifiedLiquiditySelection,
  isValidUnifiedLiquidityTokenSecurity,
  toggleUnifiedLiquiditySelections,
} from '../utils/unifiedLiquidity';

const EXTENSION_ID = 'unifiedLiquidity';
export const UNIFIED_LIQUIDITY_MARKET_SELECTOR_COLUMN_WIDTH = 60;

export type UnifiedLiquiditySelectionTarget =
  | {
      type: 'external';
      selections: MarketSelectorUnifiedLiquiditySelection[];
      onChange: (selections: MarketSelectorUnifiedLiquiditySelection[]) => void;
    }
  | { type: 'marketSelector' };

type UnifiedLiquidityViewSelectionTarget =
  | {
      type: 'external';
      selections: MarketSelectorUnifiedLiquiditySelection[];
      onChange: (selections: MarketSelectorUnifiedLiquiditySelection[]) => void;
    }
  | { type: 'marketSelector' };

/**
 * Adds granular Unified Liquidity columns to the Market Selector while keeping persisted state compact.
 *
 * For an external selection target, `selectionTarget.selections` is the canonical state owned by the consumer. It contains
 * MarketSymbol pairs only for currently selected market accounts, so consumers can persist it directly. The extension expands
 * that compact state into a transient view projection for every row:
 *
 * - selected accounts show their configured symbols;
 * - unselected sibling accounts inherit the union configured for selected accounts on the same Market;
 * - Markets without a selected account default to all available symbols;
 * - edits made to unselected rows remain local projection overrides until that account is selected.
 *
 * On a market-account transition, `onValueChange` receives the next selector value while `projectedSelections` still
 * describes the pre-transition view. Compacting that snapshot onto `nextValue.selections` lets a newly selected account
 * keep the symbols that were visible immediately before it was selected, without persisting projection-only rows.
 *
 * A `marketSelector` target does not own separate MarketSymbol state; its UL checkboxes operate on the Market Selector rows.
 */
export function useUnifiedLiquidityMarketSelectorExtensionFactory({
  enabled,
  onEnabledChange,
  showToggle = false,
  items,
  symbol,
  selectionTarget,
  unifiedLiquidityCombinePerpsAndCFDs = false,
  showSelectionListTooltip = false,
}: {
  enabled: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  showToggle?: boolean;
  items: MarketSelectorItem[];
  symbol: string | undefined;
  selectionTarget: UnifiedLiquiditySelectionTarget;
  unifiedLiquidityCombinePerpsAndCFDs?: boolean;
  showSelectionListTooltip?: boolean;
}): MarketSelectorExtensionFactory | undefined {
  const ulResponse = useUnifiedLiquidity(enabled || showSelectionListTooltip ? symbol : undefined);
  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const primarySecurity = symbol ? securitiesBySymbol.get(symbol) : undefined;

  // Build the complete pair inventory shown by the grid. This describes what can be selected, not what is persisted.
  const unifiedLiquidityColumns = useMemo(
    () => getUnifiedLiquidityColumnsForItems(primarySecurity, ulResponse?.Tokens ?? [], securitiesBySymbol, items),
    [items, primarySecurity, securitiesBySymbol, ulResponse?.Tokens]
  );
  const selectionsByItem = useMemo(() => {
    if (!(enabled || showSelectionListTooltip) || !ulResponse?.Tokens.length || !unifiedLiquidityColumns.length) {
      return undefined;
    }

    const symbols = getUnifiedLiquiditySymbols(primarySecurity, ulResponse.Tokens);
    const availableColumnIDs = new Set(unifiedLiquidityColumns.map(column => column.id));
    const result = new Map<string, UnifiedLiquidityColumnsByID>();
    for (const item of items) {
      const columnsForItem = getUnifiedLiquidityColumnsForMarket({
        availableColumnIDs,
        item,
        securitiesBySymbol,
        symbols,
      });
      if (Object.keys(columnsForItem).length > 0) {
        result.set(item.name, columnsForItem);
      }
    }
    return result;
  }, [
    enabled,
    items,
    primarySecurity,
    securitiesBySymbol,
    showSelectionListTooltip,
    ulResponse?.Tokens,
    unifiedLiquidityColumns,
  ]);

  const availableSelections = useMemo(
    () =>
      selectionsByItem
        ? Array.from(selectionsByItem.values()).flatMap(selectionsByColumn => Object.values(selectionsByColumn).flat())
        : [],
    [selectionsByItem]
  );
  const projectionContextKey = useMemo(
    () => `${symbol ?? ''}|${availableSelections.map(getUnifiedLiquiditySelectionKey).join(',')}`,
    [availableSelections, symbol]
  );
  // Preserve checkbox edits on unselected rows, but discard them when the symbol or available pair inventory changes.
  const [projectionOverridesState, setProjectionOverridesState] = useState<{
    contextKey: string;
    byMarketAccount: Map<string, Set<string>>;
  }>(() => ({ contextKey: projectionContextKey, byMarketAccount: new Map() }));
  const projectionOverrides = useMemo(
    () =>
      projectionOverridesState.contextKey === projectionContextKey
        ? projectionOverridesState.byMarketAccount
        : new Map<string, Set<string>>(),
    [projectionContextKey, projectionOverridesState]
  );
  const getTooltipMarketAccount = useTooltipMarketAccount(items, marketAccountsByMarket);

  return useMemo<MarketSelectorExtensionFactory | undefined>(() => {
    if (!enabled && !showToggle && !showSelectionListTooltip) {
      return undefined;
    }

    return api => {
      const columnIDs = unifiedLiquidityColumns.map(column => column.id);
      const projectedSelections =
        selectionTarget.type === 'external'
          ? getProjectedUnifiedLiquiditySelections({
              availableSelections,
              configuredSelections: selectionTarget.selections,
              items,
              marketSelectorSelections: api.selections,
              projectionOverrides,
            })
          : [];
      const viewSelectionTarget: UnifiedLiquidityViewSelectionTarget =
        selectionTarget.type === 'external'
          ? {
              type: 'external',
              selections: projectedSelections,
              onChange: nextProjectedSelections => {
                const selectedMarketAccounts = getSelectedMarketAccountNames(api.selections, items);
                const nextSelectionsByMarketAccount =
                  groupUnifiedLiquiditySymbolsByMarketAccount(nextProjectedSelections);
                // Unselected rows belong only to the view projection; retain their edits locally instead of persisting them.
                setProjectionOverridesState({
                  contextKey: projectionContextKey,
                  byMarketAccount: new Map(
                    Array.from(new Set(availableSelections.map(selection => selection.marketAccount)))
                      .filter(marketAccount => !selectedMarketAccounts.has(marketAccount))
                      .map(marketAccount => [
                        marketAccount,
                        nextSelectionsByMarketAccount.get(marketAccount) ?? new Set(),
                      ])
                  ),
                });

                const nextSelections = nextProjectedSelections.filter(selection =>
                  selectedMarketAccounts.has(selection.marketAccount)
                );
                // The consumer receives only canonical pairs belonging to selected market accounts.
                if (!isEqual(nextSelections, selectionTarget.selections ?? [])) {
                  selectionTarget.onChange(nextSelections);
                }
              },
            }
          : selectionTarget;
      const selectionList = getUnifiedLiquiditySelectionListContribution({
        enabled: showSelectionListTooltip,
        getTooltipMarketAccount,
        primarySecurity,
        selectionTarget: viewSelectionTarget,
        securitiesBySymbol,
        symbol,
        tokens: ulResponse?.Tokens ?? [],
      });
      return {
        id: EXTENSION_ID,
        columns: enabled
          ? getUnifiedLiquidityColumnGroups(
              unifiedLiquidityColumns,
              api,
              viewSelectionTarget,
              unifiedLiquidityCombinePerpsAndCFDs
            )
          : [],
        simpleModeVisibleColIDs: enabled ? columnIDs : [],
        advancedModeVisibleColIDs: enabled ? columnIDs : [],
        dataMap: enabled ? getUnifiedLiquidityExtensionDataMap(selectionsByItem, api, viewSelectionTarget) : undefined,
        interactiveColumnIDs: enabled ? columnIDs : [],
        selectionList,
        onValueChange:
          selectionTarget.type === 'external'
            ? nextValue => {
                const selectedMarketAccounts = getSelectedMarketAccountNames(nextValue.selections, items);
                const nextSelections = projectedSelections.filter(selection =>
                  selectedMarketAccounts.has(selection.marketAccount)
                );
                if (!isEqual(nextSelections, selectionTarget.selections)) {
                  selectionTarget.onChange(nextSelections);
                }
              }
            : undefined,
        renderBlotterControl:
          showToggle && onEnabledChange
            ? options => (
                <UnifiedLiquiditySymbolsToggle
                  enabled={enabled}
                  onEnabledChange={onEnabledChange}
                  stacked={options?.stacked}
                />
              )
            : undefined,
      };
    };
  }, [
    availableSelections,
    enabled,
    getTooltipMarketAccount,
    items,
    onEnabledChange,
    projectionContextKey,
    projectionOverrides,
    primarySecurity,
    selectionTarget,
    selectionsByItem,
    securitiesBySymbol,
    showToggle,
    showSelectionListTooltip,
    symbol,
    ulResponse?.Tokens,
    unifiedLiquidityColumns,
    unifiedLiquidityCombinePerpsAndCFDs,
  ]);
}

function UnifiedLiquiditySymbolsToggle({
  enabled,
  onEnabledChange,
  stacked = false,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  stacked?: boolean;
}) {
  const mixpanel = useMixpanel();
  const handleChange = useCallbackRef((nextEnabled: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleULSymbols, {
      [MixpanelEventProperty.Enabled]: nextEnabled,
    });
    onEnabledChange(nextEnabled);
  });

  return (
    <Toggle
      size={FormControlSizes.Small}
      checked={enabled}
      onChange={handleChange}
      justifyContent={stacked ? 'space-between' : undefined}
      label="UL Symbols"
      data-testid="market-selector-ul-symbols-toggle"
    />
  );
}

type UnifiedLiquidityColumnsByID = Record<string, MarketSelectorUnifiedLiquiditySelection[]>;
type UnifiedLiquidityExtensionData = Record<string, UnifiedLiquidityCellData>;
type UnifiedLiquidityCellData = {
  selections: MarketSelectorUnifiedLiquiditySelection[];
  selectedCount: number;
};
type UnifiedLiquidityCheckboxState = 'checked' | 'indeterminate' | 'unchecked';

interface UnifiedLiquidityColumn {
  id: string;
  group: UnifiedLiquidityColumnGroup;
  quoteCurrency: string;
}

const UnifiedLiquidityColumnGroup = {
  Spot: 'SPOT',
  PerpCfd: 'PERP/CFD',
} as const;
type UnifiedLiquidityColumnGroup = (typeof UnifiedLiquidityColumnGroup)[keyof typeof UnifiedLiquidityColumnGroup];

/**
 * Expands canonical selected-account state into the checkbox state rendered for all available MarketSymbol pairs.
 * Projection-only selections never leave this extension unless their market account subsequently becomes selected.
 */
function getProjectedUnifiedLiquiditySelections({
  availableSelections,
  configuredSelections,
  items,
  marketSelectorSelections,
  projectionOverrides,
}: {
  availableSelections: MarketSelectorUnifiedLiquiditySelection[];
  configuredSelections: MarketSelectorUnifiedLiquiditySelection[];
  items: MarketSelectorItem[];
  marketSelectorSelections: string[];
  projectionOverrides: Map<string, Set<string>>;
}) {
  const selectedMarketAccounts = getSelectedMarketAccountNames(marketSelectorSelections, items);
  const marketByMarketAccount = new Map(
    items.flatMap(item => (item.type === 'MarketAccount' ? [[item.marketAccount.Name, item.market.Name] as const] : []))
  );
  const configuredSymbolsByMarketAccount = groupUnifiedLiquiditySymbolsByMarketAccount(configuredSelections);
  const projectedSymbolsBySelectedMarket = new Map<string, Set<string>>();
  for (const marketAccount of selectedMarketAccounts) {
    const market = marketByMarketAccount.get(marketAccount);
    if (!market) {
      continue;
    }
    const projectedSymbols = projectedSymbolsBySelectedMarket.get(market) ?? new Set<string>();
    const configuredSymbols = configuredSymbolsByMarketAccount.get(marketAccount) ?? new Set();
    configuredSymbols.forEach(symbol => projectedSymbols.add(symbol));
    projectedSymbolsBySelectedMarket.set(market, projectedSymbols);
  }

  return availableSelections.filter(selection => {
    if (selectedMarketAccounts.has(selection.marketAccount)) {
      return configuredSymbolsByMarketAccount.get(selection.marketAccount)?.has(selection.symbol) === true;
    }

    const override = projectionOverrides.get(selection.marketAccount);
    if (override) {
      return override.has(selection.symbol);
    }

    const market = marketByMarketAccount.get(selection.marketAccount);
    const selectedMarketProjection = market ? projectedSymbolsBySelectedMarket.get(market) : undefined;
    // With no selected account to project from, show every available symbol as the default for a future selection.
    return selectedMarketProjection ? selectedMarketProjection.has(selection.symbol) : true;
  });
}

function getSelectedMarketAccountNames(selections: string[], items: MarketSelectorItem[]) {
  const selectedItems = new Set(selections);
  return new Set(
    items.flatMap(item =>
      item.type === 'MarketAccount' && selectedItems.has(item.name) ? [item.marketAccount.Name] : []
    )
  );
}

function groupUnifiedLiquiditySymbolsByMarketAccount(selections: MarketSelectorUnifiedLiquiditySelection[]) {
  const result = new Map<string, Set<string>>();
  for (const selection of selections) {
    const symbols = result.get(selection.marketAccount) ?? new Set<string>();
    symbols.add(selection.symbol);
    result.set(selection.marketAccount, symbols);
  }
  return result;
}

function getUnifiedLiquidityExtensionDataMap(
  selectionsByItem: Map<string, UnifiedLiquidityColumnsByID> | undefined,
  api: MarketSelectorAPI,
  selectionTarget: UnifiedLiquidityViewSelectionTarget
): Map<string, UnifiedLiquidityExtensionData> | undefined {
  if (!selectionsByItem) {
    return undefined;
  }

  return new Map(
    Array.from(selectionsByItem, ([itemName, selectionsByColumn]) => [
      itemName,
      Object.fromEntries(
        Object.entries(selectionsByColumn).map(([columnID, selections]) => [
          columnID,
          {
            selections,
            selectedCount: selections.filter(selection => isSelected(selection, api, selectionTarget)).length,
          },
        ])
      ),
    ])
  );
}

function isSelected(
  selection: MarketSelectorUnifiedLiquiditySelection,
  api: MarketSelectorAPI,
  selectionTarget: UnifiedLiquidityViewSelectionTarget
) {
  return selectionTarget.type === 'external'
    ? hasUnifiedLiquiditySelection(selectionTarget.selections, selection)
    : api.selectionsSet.has(selection.marketAccount);
}

function getUnifiedLiquidityColumns(
  symbols: UnifiedLiquidityToken[],
  securitiesBySymbol: Map<string, Security>
): UnifiedLiquidityColumn[] {
  const columns = compact(
    symbols.map(symbol => {
      const security = securitiesBySymbol.get(symbol.Symbol);
      if (!isValidUnifiedLiquidityTokenSecurity(security)) {
        return undefined;
      }

      const group = getUnifiedLiquidityColumnGroup(security);
      return group
        ? {
            id: getUnifiedLiquidityColumnID(group, security.QuoteCurrency),
            group,
            quoteCurrency: security.QuoteCurrency,
          }
        : undefined;
    })
  );

  const groupOrder = new Map<UnifiedLiquidityColumnGroup, number>([
    [UnifiedLiquidityColumnGroup.Spot, 0],
    [UnifiedLiquidityColumnGroup.PerpCfd, 1],
  ]);
  return uniqBy(columns, column => column.id).toSorted((a, b) => {
    const groupCompare = (groupOrder.get(a.group) ?? 0) - (groupOrder.get(b.group) ?? 0);
    return groupCompare || a.quoteCurrency.localeCompare(b.quoteCurrency);
  });
}

function getUnifiedLiquidityColumnsForItems(
  primarySecurity: Security | undefined,
  tokens: UnifiedLiquidityToken[],
  securitiesBySymbol: Map<string, Security>,
  items: MarketSelectorItem[]
): UnifiedLiquidityColumn[] {
  const itemMarkets = new Set(
    items
      .filter((item): item is Extract<MarketSelectorItem, { type: 'MarketAccount' }> => item.marketAccount != null)
      .map(item => item.market.Name)
  );
  return getUnifiedLiquidityColumns(
    getUnifiedLiquiditySymbols(primarySecurity, tokens).filter(symbol => itemMarkets.has(symbol.Market)),
    securitiesBySymbol
  );
}

function getUnifiedLiquidityColumnGroup(security: Security | undefined): UnifiedLiquidityColumnGroup | undefined {
  switch (security?.ProductType) {
    case ProductTypeEnum.Spot:
      return UnifiedLiquidityColumnGroup.Spot;
    case ProductTypeEnum.PerpetualSwap:
    case ProductTypeEnum.CFD:
    case ProductTypeEnum.Future:
    case ProductTypeEnum.Basis:
    case ProductTypeEnum.Option:
    case ProductTypeEnum.CalendarSpread:
    case ProductTypeEnum.Synthetic:
      return UnifiedLiquidityColumnGroup.PerpCfd;
    default:
      return undefined;
  }
}

function getUnifiedLiquidityColumnsForMarket({
  availableColumnIDs,
  item,
  securitiesBySymbol,
  symbols,
}: {
  availableColumnIDs: Set<string>;
  item: MarketSelectorItem;
  securitiesBySymbol: Map<string, Security>;
  symbols: UnifiedLiquidityToken[];
}) {
  return symbols.reduce<UnifiedLiquidityColumnsByID>((result, symbol) => {
    if (symbol.Market !== item.market.Name || !item.marketAccount) {
      return result;
    }

    const security = securitiesBySymbol.get(symbol.Symbol);
    if (!isValidUnifiedLiquidityTokenSecurity(security)) {
      return result;
    }

    const group = getUnifiedLiquidityColumnGroup(security);
    if (!group) {
      return result;
    }

    const columnID = getUnifiedLiquidityColumnID(group, security.QuoteCurrency);
    if (!availableColumnIDs.has(columnID)) {
      return result;
    }

    return {
      ...result,
      [columnID]: [...(result[columnID] ?? []), { marketAccount: item.marketAccount.Name, symbol: symbol.Symbol }],
    };
  }, {});
}

function getUnifiedLiquiditySymbols(
  primarySecurity: Security | undefined,
  tokens: UnifiedLiquidityToken[]
): UnifiedLiquidityToken[] {
  if (!tokens.length) {
    return [];
  }

  const primarySymbols =
    primarySecurity?.Markets?.map(market => ({ Market: market, Symbol: primarySecurity.Symbol })) ?? [];
  return [...primarySymbols, ...tokens];
}

function getUnifiedLiquidityColumnGroups(
  columns: UnifiedLiquidityColumn[],
  api: MarketSelectorAPI,
  selectionTarget: UnifiedLiquidityViewSelectionTarget,
  unifiedLiquidityCombinePerpsAndCFDs: boolean
): ColumnGroup[] {
  const cellRenderer = createUnifiedLiquidityCellRenderer(api, selectionTarget);
  return Object.values(UnifiedLiquidityColumnGroup)
    .map(group => ({ group, columns: columns.filter(column => column.group === group) }))
    .filter(({ columns: groupColumns }) => groupColumns.length > 0)
    .map(({ group, columns: groupColumns }) => ({
      type: COLUMN_GROUP_NATIVE_TYPE,
      groupId: `ul-${group}`,
      headerName: getUnifiedLiquidityColumnGroupLabel(group, unifiedLiquidityCombinePerpsAndCFDs),
      children: groupColumns.map(column => ({
        type: 'custom',
        id: column.id,
        field: `extensionData.${EXTENSION_ID}.${column.id}`,
        title: column.quoteCurrency,
        width: UNIFIED_LIQUIDITY_MARKET_SELECTOR_COLUMN_WIDTH,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        params: {
          cellRenderer,
          headerComponent: UnifiedLiquidityColumnHeader,
          headerComponentParams: {
            columnID: column.id,
            quoteCurrency: column.quoteCurrency,
            marketSelectorAPI: api,
            selectionTarget,
          } satisfies UnifiedLiquidityColumnHeaderParams,
        },
      })),
    }));
}

function getUnifiedLiquidityColumnGroupLabel(
  group: UnifiedLiquidityColumnGroup,
  unifiedLiquidityCombinePerpsAndCFDs: boolean
) {
  if (group === UnifiedLiquidityColumnGroup.PerpCfd) {
    return unifiedLiquidityCombinePerpsAndCFDs ? 'PERP/SWAP' : 'PERP';
  }
  return group;
}

function createUnifiedLiquidityCellRenderer(
  api: MarketSelectorAPI,
  selectionTarget: UnifiedLiquidityViewSelectionTarget
) {
  return function UnifiedLiquidityCellRenderer(
    params: ICellRendererParams<MarketSelectorRow, UnifiedLiquidityCellData | undefined>
  ) {
    const row = params.node.data;
    if (!row) {
      if (params.node.level !== 0 || !params.column) {
        return null;
      }

      const selections = getGroupSelections(params, params.column.getColId());
      if (!selections.length) {
        return '-';
      }

      const selectedCount = selections.filter(selection => isSelected(selection, api, selectionTarget)).length;
      return (
        <UnifiedLiquidityCheckbox
          muted={!isUnifiedLiquidityGroupRowSelected(params)}
          checked={selectedCount === selections.length}
          indeterminate={selectedCount > 0 && selectedCount < selections.length}
          onChange={checked => {
            if (selectionTarget.type === 'external') {
              selectionTarget.onChange(
                toggleUnifiedLiquiditySelections(selectionTarget.selections, selections, checked)
              );
            } else if (params.node.key) {
              api.selectMarket(params.node.key, checked);
            }
          }}
        />
      );
    }

    const value = params.value;
    if (!value?.selections.length) {
      return '-';
    }

    return (
      <UnifiedLiquidityCheckbox
        muted={!row.selected}
        checked={value.selectedCount === value.selections.length}
        indeterminate={value.selectedCount > 0 && value.selectedCount < value.selections.length}
        onChange={checked => {
          if (selectionTarget.type === 'external') {
            selectionTarget.onChange(
              toggleUnifiedLiquiditySelections(selectionTarget.selections, value.selections, checked)
            );
          } else {
            api.setSelected(row.key, checked);
          }
        }}
      />
    );
  };
}

function isUnifiedLiquidityGroupRowSelected(params: ICellRendererParams<MarketSelectorRow>) {
  return params.node.allLeafChildren?.some(childNode => childNode.data?.selected) ?? false;
}

interface UnifiedLiquidityColumnHeaderParams {
  columnID: string;
  quoteCurrency: string;
  marketSelectorAPI: MarketSelectorAPI;
  selectionTarget: UnifiedLiquidityViewSelectionTarget;
}

function UnifiedLiquidityColumnHeader(params: IHeaderParams<MarketSelectorRow> & UnifiedLiquidityColumnHeaderParams) {
  const checkboxState = getUnifiedLiquidityColumnCheckboxState(
    params.api,
    params.columnID,
    params.marketSelectorAPI,
    params.selectionTarget
  );

  const handleCheckboxChange = useCallbackRef((checked: boolean) => {
    const columnTargets = getUnifiedLiquidityColumnTargets(params.api, params.columnID);

    if (params.selectionTarget.type === 'external') {
      params.selectionTarget.onChange(
        toggleUnifiedLiquiditySelections(params.selectionTarget.selections, columnTargets.selections, checked)
      );
    } else if (checked) {
      params.marketSelectorAPI.selectAll(columnTargets.items);
    } else {
      params.marketSelectorAPI.unselectAll(columnTargets.items);
    }
  });

  return (
    <HStack
      gap="spacing4"
      justifyContent="center"
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
    >
      <UnifiedLiquidityHeaderCheckboxMouseTrap>
        <Checkbox
          data-testid={`market-selector-unified-liquidity-header-checkbox-${params.columnID}`}
          checked={checkboxState === 'checked'}
          indeterminate={checkboxState === 'indeterminate'}
          onChange={event => {
            event.stopPropagation();
            handleCheckboxChange((event.target as HTMLInputElement).checked);
          }}
        />
      </UnifiedLiquidityHeaderCheckboxMouseTrap>
      <Text>{params.quoteCurrency}</Text>
    </HStack>
  );
}

export function getUnifiedLiquidityColumnCheckboxState(
  gridApi: GridApi<MarketSelectorRow>,
  columnID: string,
  marketSelectorAPI: MarketSelectorAPI,
  selectionTarget: UnifiedLiquidityViewSelectionTarget
): UnifiedLiquidityCheckboxState {
  const { selections } = getUnifiedLiquidityColumnTargets(gridApi, columnID);

  if (selections.length === 0) {
    return 'unchecked';
  }

  const selectedCount = selections.filter(selection =>
    isSelected(selection, marketSelectorAPI, selectionTarget)
  ).length;

  if (selectedCount === 0) {
    return 'unchecked';
  }

  return selectedCount === selections.length ? 'checked' : 'indeterminate';
}

function getUnifiedLiquidityColumnTargets(gridApi: GridApi<MarketSelectorRow>, columnID: string) {
  const selectionsByKey = new Map<string, MarketSelectorUnifiedLiquiditySelection>();
  const itemsByKey = new Map<string, MarketSelectorItem>();

  gridApi.forEachNode(node => {
    const row = node.data;
    if (!row) {
      return;
    }

    const extensionData = row.extensionData[EXTENSION_ID] as UnifiedLiquidityExtensionData | undefined;
    const selections = extensionData?.[columnID]?.selections ?? [];
    if (!selections.length) {
      return;
    }

    itemsByKey.set(row.key, row.item);
    for (const selection of selections) {
      selectionsByKey.set(`${selection.marketAccount}|${selection.symbol}`, selection);
    }
  });

  return {
    selections: Array.from(selectionsByKey.values()),
    items: Array.from(itemsByKey.values()),
  };
}

function UnifiedLiquidityCheckbox({
  checked,
  indeterminate,
  muted = false,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  muted?: boolean;
  onChange: (checked: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // AG Grid's row-click handler runs before React's bubbled change handler, so stop this input natively.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const handleClick = (event: MouseEvent) => event.stopPropagation();
    const handleChange = (event: Event) => {
      event.stopPropagation();
      onChange((event.target as HTMLInputElement).checked);
    };

    input.addEventListener('click', handleClick);
    input.addEventListener('change', handleChange);
    return () => {
      input.removeEventListener('click', handleClick);
      input.removeEventListener('change', handleChange);
    };
  }, [onChange]);

  return (
    <UnifiedLiquidityCheckboxWrapper
      $muted={muted}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
    >
      <Checkbox checked={checked} indeterminate={indeterminate} inputRef={inputRef} onChange={noop} />
    </UnifiedLiquidityCheckboxWrapper>
  );
}

function getGroupSelections(params: ICellRendererParams<MarketSelectorRow>, columnID: string) {
  return compact(
    params.node.allLeafChildren?.flatMap(childNode => {
      const extensionData = childNode.data?.extensionData[EXTENSION_ID] as UnifiedLiquidityExtensionData | undefined;
      return extensionData?.[columnID]?.selections ?? [];
    }) ?? []
  );
}

function useTooltipMarketAccount(items: MarketSelectorItem[], marketAccountsByMarket: Map<string, MarketAccount[]>) {
  const itemsByName = useMemo(() => new Map(items.map(item => [item.name, item])), [items]);
  const marketAccountItemsByMarket = useMemo(() => {
    const result = new Map<string, MarketAccount[]>();
    for (const item of items) {
      if (item.type === 'MarketAccount') {
        result.set(item.market.Name, [...(result.get(item.market.Name) ?? []), item.marketAccount]);
      }
    }
    return result;
  }, [items]);

  return useCallback(
    (itemName: string) => {
      const item = itemsByName.get(itemName);
      if (!item) {
        return undefined;
      }
      if (item.type === 'MarketAccount') {
        return item.marketAccount;
      }

      const selectorMarketAccounts = marketAccountItemsByMarket.get(item.market.Name) ?? [];
      return selectorMarketAccounts.length === 1
        ? selectorMarketAccounts[0]
        : (marketAccountsByMarket.get(item.market.Name) ?? []).length === 1
          ? marketAccountsByMarket.get(item.market.Name)?.[0]
          : undefined;
    },
    [itemsByName, marketAccountItemsByMarket, marketAccountsByMarket]
  );
}

function getUnifiedLiquiditySelectionListContribution({
  enabled,
  getTooltipMarketAccount,
  primarySecurity,
  selectionTarget,
  securitiesBySymbol,
  symbol,
  tokens,
}: {
  enabled: boolean;
  getTooltipMarketAccount: (itemName: string) => MarketAccount | undefined;
  primarySecurity: Security | undefined;
  selectionTarget: UnifiedLiquidityViewSelectionTarget;
  securitiesBySymbol: Map<string, Security>;
  symbol: string | undefined;
  tokens: UnifiedLiquidityToken[];
}) {
  if (!enabled || !symbol || selectionTarget.type !== 'external') {
    return undefined;
  }

  return {
    selectionListItemLabelSuffix: (itemName: string) => {
      const marketAccount = getTooltipMarketAccount(itemName);
      if (!marketAccount) {
        return undefined;
      }
      const group = getUnifiedLiquidityMarketAccountTooltipGroup({
        marketAccount,
        primarySymbol: symbol,
        primarySecurity,
        unifiedLiquidityTokens: tokens,
        selectedUnifiedLiquidityMarketSymbols: selectionTarget.selections,
        securitiesBySymbol,
      });
      return (
        <UnifiedLiquidityTooltipIcon
          data-testid="market-selector-unified-liquidity-tooltip-icon"
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
        >
          <UnifiedLiquidityTooltipIndicator
            groups={[group]}
            dataTestId="market-selector-unified-liquidity-tooltip"
            statusTestIdPrefix="market-selector-unified-liquidity-tooltip-status"
            closeOnClickOutside
            interactive={false}
            isSmall={false}
          />
        </UnifiedLiquidityTooltipIcon>
      );
    },
  };
}

function getUnifiedLiquidityColumnID(group: UnifiedLiquidityColumnGroup, quoteCurrency: string) {
  return `ul-${group}-${quoteCurrency}`;
}

const UnifiedLiquidityTooltipIcon = styled(HStack)`
  cursor: default;
`;

const UnifiedLiquidityHeaderCheckboxMouseTrap = styled.span`
  display: flex;
`;

const UnifiedLiquidityCheckboxWrapper = styled.span<{ $muted: boolean }>`
  ${({ $muted, theme }) =>
    $muted &&
    `
      input[data-checked='true'] ~ label,
      input[data-indeterminate='true'] ~ label {
        background: ${theme.colors.gray['070']};
        border-color: ${theme.colors.gray['070']};
      }

      &:hover input[data-checked='true'] ~ label,
      &:hover input[data-indeterminate='true'] ~ label {
        background: ${theme.colors.gray['070']};
        border-color: ${theme.colors.gray['070']};
      }
    `}
`;
